import React, { useEffect, useMemo, useState } from 'react'
import SecurityTreeComponent from '../../components/containers/SecurityTreeComponent'
import SecurityTreeOutline from '../../components/common/SecurityTreeOutline'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'
import {
  getSecurityCheckFamiliesWithScoresById,
  getSecurityTreeByFamilyId,
  securityControlMatrix,
} from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import { calculateAverageCostForSecurityControl } from '../../redux/securityControls/securityControlsAction'
import { RootState, AppDispatch, useAppDispatch } from '../../redux/store'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import AnalysisPdfReport from '../../Pdf/AnalysisPdfReport'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatCurrency, locale, formatDecimal } from '../../utils/formatNumbers'
import {
  AnalysisKpiCard,
  AnalysisPageLoader,
} from '../../components/common/analysisLayout'
import {
  DetailButton,
  DetailPageHead,
} from '../../components/common/detail'
import '../../components/common/maturity-analysis.css'

function SecurityControlsAnalysis() {
  const { id } = useParams<{ id: string }>()
  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const actionsCost = useSelector(
    (state: RootState) =>
      state.securitycheckFamily.SecurityCheckFamilyActionsCosts?.totalCost
  )
  const SecurityTreeByFamilyId = useSelector(
    (state: RootState) => state.securitycheckFamily.SecurityTreeByFamilyId
  )
  const dataSCFScore = useSelector(
    (state: RootState) => state.securitycheckFamily.dataSCFScore
  )

  const { t, i18n } = useTranslation()

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!id) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        await Promise.all([
          dispatch(calculateAverageCostForSecurityControl(id)),
          dispatch(getSecurityTreeByFamilyId(id)),
          dispatch(
            getSecurityCheckFamiliesWithScoresById({
              id,
              language: i18n.language.toUpperCase(),
            })
          ),
          dispatch(securityControlMatrix()),
        ])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [dispatch, id, i18n.language])

  const selectedNorm = useSelector(selectSpecifications)[0]?.NormBase || ''

  const pdf = useAnalysisPdfExport()
  const currency = useSelector(selectSpecifications)[0]?.Currency

  const computedOn = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language]
  )

  const scfScore = Number(
    dataSCFScore?.data?.scfScore ?? dataSCFScore?.data?.SCFScore ?? 0
  )
  const costValue =
    actionsCost === undefined || actionsCost === null
      ? '—'
      : formatCurrency(Number(actionsCost) || 0, currency || '', {
          locale,
          useIntl: true,
        })

  const stampTitle = t('analysis.isoControlsAnalysis.computedOn', {
    date: computedOn,
  })
  const backPath = id ? `/security-controls/${id}` : `/security/${selectedNorm}`
  const entityName =
    dataSCFScore?.data?.LabelSCF ||
    SecurityTreeByFamilyId?.securityTree?.Label ||
    t('ISO security controls List', { norm: selectedNorm })
  return (
    <div className="maturity-analysis-page detail-page">
      <DetailPageHead
        eyebrow={t('analysis.isoControlsAnalysis.eyebrow')}
        title={t('analysis.isoControlsAnalysis.pageTitle')}
        subtitle={t('analysis.isoControlsAnalysis.subtitle', {
          name: entityName || '—',
          date: computedOn,
        })}
        actions={
          <>
            <DetailButton variant="ghost" onClick={() => navigate(backPath)}>
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('analysis.isoControlsAnalysis.backToControls')}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() =>
                pdf.exportPdf(entityName || 'security-controls-analysis')
              }
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={2} chartCount={1} />
      ) : (
        <>
          <div className="ma-kpi-grid">
            <AnalysisKpiCard
              label={t('charts.scfScoreCard.title')}
              value={formatDecimal(scfScore)}
              tone={scfScore > 0 ? 'teal' : 'muted'}
              stampTitle={stampTitle}
              description={t('analysis.isoControlsAnalysis.scfScoreDesc')}
            />
            <AnalysisKpiCard
              label={t('charts.scfActionsCostCard.title')}
              value={costValue}
              tone={Number(actionsCost) > 0 ? 'teal' : 'muted'}
              stampTitle={stampTitle}
              description={t('analysis.isoControlsAnalysis.actionsCostDesc')}
            />
          </div>

          <p className="ma-section-label">
            {t('analysis.isoControlsAnalysis.sectionTree')}
          </p>
          <div className="ma-card">
            <div className="ma-card-head">
              <h3>{t('charts.securityTreeSection.title')}</h3>
            </div>
            <div className="ma-card-body">
              <SecurityTreeComponent
                data={SecurityTreeByFamilyId?.securityTree}
              />
            </div>
          </div>
        </>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <AnalysisPdfReport
          ref={pdf.ref}
          eyebrow={t('analysis.isoControlsAnalysis.eyebrow')}
          title={entityName || '—'}
          subtitle={t('analysis.isoControlsAnalysis.subtitle', {
            name: entityName || '—',
            date: computedOn,
          })}
          footerTitle={t('analysis.isoControlsAnalysis.pageTitle')}
          computedOn={computedOn}
          kpis={[
            {
              label: t('charts.scfScoreCard.title'),
              value: formatDecimal(scfScore),
              desc: t('analysis.isoControlsAnalysis.scfScoreDesc'),
              tone: scfScore > 0 ? 'teal' : 'muted',
            },
            {
              label: t('charts.scfActionsCostCard.title'),
              value: costValue,
              desc: t('analysis.isoControlsAnalysis.actionsCostDesc'),
              tone: Number(actionsCost) > 0 ? 'teal' : 'muted',
            },
          ]}
          extra={
            SecurityTreeByFamilyId?.securityTree
              ? {
                  label: t('analysis.isoControlsAnalysis.sectionTree'),
                  title: t('charts.securityTreeSection.title'),
                  node: (
                    <SecurityTreeOutline
                      data={SecurityTreeByFamilyId.securityTree}
                    />
                  ),
                }
              : undefined
          }
        />
      </div>
    </div>
  )
}

export default SecurityControlsAnalysis
